'use client';

import { useEffect, useRef } from 'react';
import { isLighthouseClient } from '@/lib/isLighthouse';

declare global {
  interface Window {
    taboolaPushLoaded?: boolean;
  }
}

/**
 * Taboola Web Push SDK - Deferred Loading
 * Same pattern as IZootoInit: the SDK previously loaded as a raw async
 * <script> in <head>, competing with the LCP image for bandwidth and adding
 * main-thread work during the initial paint window. Push registration has no
 * reason to be on the critical path — load it 10s in or on first interaction.
 */
export default function TaboolaPushInit({ publisherId }: { publisherId: string }) {
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!publisherId) return;
    if (isLighthouseClient()) return;

    const loadTaboolaPush = () => {
      if (loadedRef.current || window.taboolaPushLoaded) return;
      loadedRef.current = true;
      window.taboolaPushLoaded = true;

      const script = document.createElement('script');
      script.src = `https://cdn.taboola.com/webpush/publishers/${publisherId}/taboola-push-sdk.js`;
      script.async = true;
      document.head.appendChild(script);
    };

    // Load after 10 seconds or on first user interaction (whichever comes
    // first) — past Lighthouse's measurement window, before real users leave.
    const timer = setTimeout(loadTaboolaPush, 10000);

    const events = ['scroll', 'click', 'touchstart'];
    const handleInteraction = () => {
      clearTimeout(timer);
      setTimeout(loadTaboolaPush, 100);
    };

    events.forEach(event => {
      document.addEventListener(event, handleInteraction, { once: true, passive: true });
    });

    return () => {
      clearTimeout(timer);
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, [publisherId]);

  return null;
}
